import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Input, InputNumber, Select, Checkbox, Upload, notification, Popconfirm } from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined, ChevronDownOutlined, SaveOutlined } from '@ant-design/icons';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';
import QuizFormNewV2 from '../QuizFormNewV2';
import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';

// ─── Design Tokens ─────────────────────────────────────────────────────────
const tokens = {
  bg:         '#FFFFFF',
  bgCard:     '#FFFFFF',
  bgCardAlt:  '#F8FAFC',
  bgInput:    '#FFFFFF',
  border:     '#E2E8F0',
  borderLight:'#CBD5E1',
  primary:    '#2d96e0',
  primaryDim: '#1a7bbf',
  primaryBg:  '#EBF5FC',
  gold:       '#F5A623',
  goldDim:    '#C17D10',
  teal:       '#0EA5A0',
  danger:     '#EF4444',
  textMain:   '#1A202C',
  textSub:    '#64748B',
  textMuted:  '#94A3B8',
  radius:     '12px',
  radiusSm:   '8px',
  shadow:     '0 1px 8px rgba(0,0,0,0.08)',
  shadowMd:   '0 4px 16px rgba(0,0,0,0.10)',
  shadowGlow: '0 0 20px rgba(45,150,224,0.15)',
};

// Level accent colors for left-border coding
const levelColors = ['#2d96e0', '#0EA5A0', '#F5A623', '#8B5CF6', '#F472B6', '#22C55E'];

// ─── Global Styles ──────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sarabun:wght@400;500;600&display=swap');

    .mission-edit-root {
        font-family: 'Inter', 'Sarabun', sans-serif;

        /* Override Ant Design inside our root */
        .ant-form-item-label > label {
            color: ${tokens.textSub};
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.05em;
            text-transform: uppercase;
        }
        .ant-input, .ant-input-number, .ant-select-selector, .ant-input-affix-wrapper {
            background: ${tokens.bgInput} !important;
            border-color: ${tokens.border} !important;
            color: ${tokens.textMain} !important;
            border-radius: ${tokens.radiusSm} !important;
            transition: border-color 0.2s;
            &:focus, &:hover {
                border-color: ${tokens.primary} !important;
                box-shadow: 0 0 0 2px rgba(45,150,224,0.2) !important;
            }
        }
        .ant-input-number-input { color: ${tokens.textMain} !important; }
        .ant-select-selection-item, .ant-select-arrow { color: ${tokens.textMain} !important; }
        .ant-select-dropdown {
            background: ${tokens.bgCard} !important;
            border: 1px solid ${tokens.border};
            .ant-select-item { color: ${tokens.textSub}; }
            .ant-select-item-option-active, .ant-select-item-option-selected {
                background: ${tokens.bgInput} !important;
                color: ${tokens.textMain} !important;
            }
        }
        textarea.ant-input { resize: vertical; }
        .ant-checkbox-inner {
            background: ${tokens.bgInput} !important;
            border-color: ${tokens.border} !important;
        }
        .ant-checkbox-checked .ant-checkbox-inner {
            background: ${tokens.primary} !important;
            border-color: ${tokens.primary} !important;
        }
        .ant-checkbox-wrapper { color: ${tokens.textMain}; }
        .ant-upload-list-item { color: ${tokens.textSub} !important; }
        .ant-popover-inner { background: ${tokens.bgCard}; border: 1px solid ${tokens.border}; }
        .ant-popover-message-title { color: ${tokens.textMain}; }
        .ant-tree {
            background: transparent !important;
            color: ${tokens.textSub};
            .ant-tree-node-content-wrapper:hover { background: ${tokens.bgInput} !important; }
            .ant-tree-node-selected { background: ${tokens.bgInput} !important; color: ${tokens.textMain}; }
            .ant-tree-checkbox-inner { background: ${tokens.bgInput}; border-color: ${tokens.border}; }
            .ant-tree-checkbox-checked .ant-tree-checkbox-inner { background: ${tokens.primary}; border-color: ${tokens.primary}; }
        }
    }
`;

// ─── Animations ─────────────────────────────────────────────────────────────
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

// ─── Layout ──────────────────────────────────────────────────────────────────
const Root = styled.div`
    background: ${tokens.bg};
    min-height: 100vh;
    padding: 32px 40px;
    animation: ${fadeIn} 0.3s ease;

    @media (max-width: 768px) { padding: 16px; }
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid ${tokens.border};
`;

const PageTitle = styled.h1`
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: ${tokens.textMain};
    letter-spacing: -0.3px;

    span {
        color: ${tokens.primary};
        margin-left: 8px;
        font-weight: 500;
        font-size: 16px;
        opacity: 0.8;
    }
`;

const SectionCard = styled.div`
    background: ${tokens.bgCard};
    border: 1px solid ${tokens.border};
    border-radius: ${tokens.radius};
    margin-bottom: 24px;
    box-shadow: ${tokens.shadow};
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid ${tokens.border};
    background: ${tokens.bgCardAlt};
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.textMain};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
        content: '';
        display: block;
        width: 4px;
        height: 16px;
        background: ${tokens.primary};
        border-radius: 2px;
    }
`;

const SectionBody = styled.div`
    padding: 24px;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const Btn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: ${tokens.radiusSm};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 8px 16px;
    transition: all 0.18s ease;
    outline: none;
    white-space: nowrap;

    &:active { transform: scale(0.97); }
`;

const PrimaryBtn = styled(Btn)`
    background: ${tokens.primary};
    color: #fff;
    box-shadow: 0 2px 12px rgba(45,150,224,0.30);
    &:hover { background: ${tokens.primaryDim}; box-shadow: 0 4px 18px rgba(45,150,224,0.45); }
`;

const GhostBtn = styled(Btn)`
    background: transparent;
    color: ${tokens.primary};
    border: 1px solid ${tokens.primary};
    &:hover { background: rgba(45,150,224,0.08); }
`;

const DangerBtn = styled(Btn)`
    background: transparent;
    color: ${tokens.danger};
    border: 1px solid transparent;
    padding: 4px 8px;
    font-size: 12px;
    &:hover { background: rgba(245,101,101,0.12); border-color: ${tokens.danger}; }
`;

// ─── Grid ────────────────────────────────────────────────────────────────────
const Grid2 = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${tokens.textSub};
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 6px;
`;

const FieldWrap = styled.div`
    margin-bottom: 20px;
`;

// ─── Level & Task Panels ──────────────────────────────────────────────────────
const LevelList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const LevelPanel = styled.div`
    border: 1px solid ${tokens.border};
    border-left: 4px solid ${({ $color }) => $color || tokens.primary};
    border-radius: ${tokens.radiusSm};
    background: ${tokens.bgCardAlt};
    overflow: hidden;
    box-shadow: ${tokens.shadow};
    animation: ${fadeIn} 0.25s ease;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
    &:hover { background: rgba(255,255,255,0.03); }
`;

const PanelTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    font-size: 14px;
    color: ${tokens.textMain};
`;

const PanelChevron = styled.span`
    color: ${tokens.textMuted};
    transition: transform 0.2s;
    transform: ${({ $open }) => $open ? 'rotate(90deg)' : 'rotate(0deg)'};
    display: flex;
    align-items: center;
`;

const PanelBody = styled.div`
    padding: 20px;
    border-top: 1px solid ${tokens.border};
    display: ${({ $open }) => $open ? 'block' : 'none'};
`;

const LevelBadge = styled.span`
    background: ${({ $color }) => $color || tokens.primary}22;
    color: ${({ $color }) => $color || tokens.primary};
    border: 1px solid ${({ $color }) => $color || tokens.primary}44;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const TaskList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
`;

const TaskPanel = styled.div`
    border: 1px solid ${tokens.borderLight};
    border-radius: ${tokens.radiusSm};
    background: ${tokens.bgInput};
    overflow: hidden;
    animation: ${fadeIn} 0.2s ease;
`;

const TaskHeader = styled(PanelHeader)`
    padding: 11px 16px;
    background: transparent;
`;

const TaskBody = styled(PanelBody)`
    background: transparent;
    padding: 16px;
`;

// ─── Coin Badge ───────────────────────────────────────────────────────────────
const CoinBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: ${tokens.gold}18;
    color: ${tokens.gold};
    border: 1px solid ${tokens.gold}44;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
    box-shadow: ${({ $glow }) => $glow ? `0 0 10px ${tokens.gold}44` : 'none'};

    &::before {
        content: '◈';
        font-size: 11px;
    }
`;

// ─── Loading / Empty ──────────────────────────────────────────────────────────
const Loader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: ${tokens.textMuted};
    font-size: 14px;
    gap: 12px;

    &::before {
        content: '';
        width: 20px;
        height: 20px;
        border: 2px solid ${tokens.border};
        border-top-color: ${tokens.primary};
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${tokens.border};
    margin: 20px 0;
`;

const RowBetween = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
`;

const SubHeading = styled.div`
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${tokens.textSub};
`;

// ─── Partner Tree Override ────────────────────────────────────────────────────
const PartnerBox = styled.div`
    border: 1px solid ${tokens.border};
    border-radius: ${tokens.radiusSm};
    padding: 12px;
    background: ${tokens.bgInput};
    max-height: 280px;
    overflow-y: auto;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: ${tokens.border}; border-radius: 2px; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function StyledInput({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '8px 12px',
        background: tokens.bgInput, border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radiusSm, color: tokens.textMain,
        fontSize: 14, fontFamily: 'inherit', outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box',
      }}
      onFocus={e => e.target.style.borderColor = tokens.primary}
      onBlur={e => e.target.style.borderColor = tokens.border}
      {...rest}
    />
  );
}

function StyledTextarea({ value, onChange, placeholder, rows = 3, ...rest }) {
  return (
    <textarea
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', padding: '8px 12px',
        background: tokens.bgInput, border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radiusSm, color: tokens.textMain,
        fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
        transition: 'border-color 0.2s', boxSizing: 'border-box',
      }}
      onFocus={e => e.target.style.borderColor = tokens.primary}
      onBlur={e => e.target.style.borderColor = tokens.border}
      {...rest}
    />
  );
}

function StyledSelect({ value, onChange, options, ...rest }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '8px 12px',
        background: tokens.bgInput, border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radiusSm, color: tokens.textMain,
        fontSize: 14, fontFamily: 'inherit', outline: 'none',
        cursor: 'pointer', appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748B' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
      }}
      {...rest}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function StyledNumber({ value, onChange, min = 0, ...rest }) {
  return (
    <input
      type="number" min={min}
      value={value ?? ''}
      onChange={e => onChange(Number(e.target.value))}
      style={{
        width: '100%', padding: '8px 12px',
        background: tokens.bgInput, border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radiusSm, color: tokens.textMain,
        fontSize: 14, fontFamily: 'inherit', outline: 'none',
        transition: 'border-color 0.2s', boxSizing: 'border-box',
      }}
      onFocus={e => e.target.style.borderColor = tokens.primary}
      onBlur={e => e.target.style.borderColor = tokens.border}
      {...rest}
    />
  );
}

// ─── Collapsible wrapper ──────────────────────────────────────────────────────
function Collapsible({ header, children, defaultOpen = false, accent }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <LevelPanel $color={accent}>
      <PanelHeader onClick={() => setOpen(v => !v)}>
        {header({ open })}
        <PanelChevron $open={open}><RightOutlined style={{ fontSize: 12 }} /></PanelChevron>
      </PanelHeader>
      <PanelBody $open={open}>{children}</PanelBody>
    </LevelPanel>
  );
}

function TaskCollapsible({ header, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <TaskPanel>
      <TaskHeader onClick={() => setOpen(v => !v)}>
        {header({ open })}
        <PanelChevron $open={open}><RightOutlined style={{ fontSize: 11 }} /></PanelChevron>
      </TaskHeader>
      <TaskBody $open={open}>{children}</TaskBody>
    </TaskPanel>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MissionEditApp = ({ missionId, partnerGroups }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [missionData, setMissionData] = useState(null);
  const [formValues, setFormValues]   = useState({});
  const [checkedPartners, setCheckedPartners] = useState([]);

  useEffect(() => { fetchData(); }, [missionId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/admins/api/mission_v2s/${missionId}.json`);
      setMissionData(res.data);
      setFormValues(res.data);
      setCheckedPartners(res.data.partner_ids || []);
    } catch (err) {
      notification.error({ message: 'โหลดข้อมูลไม่สำเร็จ', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const cleanedPartnerIds = checkedPartners.filter(id => !String(id).startsWith('group-'));
    const payload = { ...missionData, ...formValues, partner_ids: cleanedPartnerIds };
    try {
      await axios.put(`/admins/api/mission_v2s/${missionId}.json`, { mission_v2: payload });
      notification.success({ message: '✓ บันทึกสำเร็จ' });
      fetchData();
    } catch (err) {
      notification.error({ message: 'บันทึกไม่สำเร็จ', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => setFormValues(v => ({ ...v, [key]: value }));

  // ─ Level/Task mutations
  const addLevel = () => setMissionData(produce(draft => {
    if (!draft.level_v2s) draft.level_v2s = [];
    draft.level_v2s.push({ id: `new_${uuidv4()}`, name: 'New Level', no: draft.level_v2s.length + 1, task_v2s: [], _destroy: false });
  }));

  const deleteLevel = (levelId) => setMissionData(produce(draft => {
    const l = draft.level_v2s.find(x => x.id === levelId);
    if (!l) return;
    if (typeof l.id === 'string' && l.id.startsWith('new_')) draft.level_v2s = draft.level_v2s.filter(x => x.id !== levelId);
    else l._destroy = true;
  }));

  const addTask = (levelId) => setMissionData(produce(draft => {
    const level = draft.level_v2s.find(x => x.id === levelId);
    if (!level) return;
    if (!level.task_v2s) level.task_v2s = [];
    level.task_v2s.push({ id: `new_${uuidv4()}`, name: 'New Task', no: level.task_v2s.length + 1, coin: 0, quiz_v2s_attributes: [], _destroy: false });
  }));

  const deleteTask = (levelId, taskId) => setMissionData(produce(draft => {
    const level = draft.level_v2s.find(x => x.id === levelId);
    if (!level) return;
    const t = level.task_v2s.find(x => x.id === taskId);
    if (!t) return;
    if (typeof t.id === 'string' && t.id.startsWith('new_')) level.task_v2s = level.task_v2s.filter(x => x.id !== taskId);
    else t._destroy = true;
  }));

  const updateLevel = (levelId, key, value) => setMissionData(produce(draft => {
    const l = draft.level_v2s.find(x => x.id === levelId);
    if (l) l[key] = value;
  }));

  const updateTask = (levelId, taskId, key, value) => setMissionData(produce(draft => {
    const l = draft.level_v2s.find(x => x.id === levelId);
    if (!l) return;
    const t = l.task_v2s.find(x => x.id === taskId);
    if (t) t[key] = value;
  }));

  const updateTaskQuizzes = (levelId, taskId, quizzes) => updateTask(levelId, taskId, 'quiz_v2s_attributes', quizzes);

  // Partner tree (simple checkbox list)
  const togglePartner = (id) => setCheckedPartners(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const toggleGroup = (groupPartners) => {
    const ids = groupPartners.map(p => p.id);
    const allChecked = ids.every(id => checkedPartners.includes(id));
    setCheckedPartners(prev => allChecked
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]
    );
  };

  const categoryOptions = [
    { value: 'financial', label: 'การเงิน' },
    { value: 'career',    label: 'การงาน' },
    { value: 'health',    label: 'สุขภาพ' },
    { value: 'relationships', label: 'ความสัมพันธ์' },
    { value: 'social',    label: 'สังคม' },
    { value: 'other',     label: 'อื่นๆ' },
  ];

  if (loading) return (
    <Root className="mission-edit-root">
      <GlobalStyle />
      <Loader>กำลังโหลดข้อมูล...</Loader>
    </Root>
  );

  if (!missionData) return <Root className="mission-edit-root"><GlobalStyle /><Loader>ไม่พบข้อมูล Mission</Loader></Root>;

  const activeLevels = (missionData.level_v2s || []).filter(l => !l._destroy);

  return (
    <Root className="mission-edit-root">
      <GlobalStyle />

      {/* ── Page Header ── */}
      <PageHeader>
        <PageTitle>
          แก้ไข Mission
          <span>#{missionId}</span>
        </PageTitle>
        <PrimaryBtn onClick={handleSave} disabled={saving}>
          {saving ? '⏳ กำลังบันทึก...' : '💾 บันทึกทั้งหมด'}
        </PrimaryBtn>
      </PageHeader>

      {/* ── Mission Info ── */}
      <SectionCard>
        <SectionHeader>
          <SectionTitle>ข้อมูลภารกิจหลัก</SectionTitle>
        </SectionHeader>
        <SectionBody>
          <Grid2>
            {/* Left column */}
            <div>
              <FieldWrap>
                <FieldLabel>รูป Badge สี</FieldLabel>
                <Upload beforeUpload={() => false} maxCount={1} listType="picture"
                        onChange={({ fileList }) => setField('badge_color', fileList)}
                >
                  <button style={{
                    display:'flex', alignItems:'center', gap:8, padding:'8px 14px',
                    background: tokens.bgInput, border:`1px dashed ${tokens.border}`,
                    borderRadius: tokens.radiusSm, color: tokens.textSub,
                    cursor:'pointer', fontFamily:'inherit', fontSize:13,
                  }}>
                    <UploadOutlined /> อัปโหลดรูป
                  </button>
                </Upload>
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>ชื่อภารกิจ *</FieldLabel>
                <StyledInput value={formValues.name} onChange={e => setField('name', e.target.value)} placeholder="ชื่อภารกิจที่จะแสดงให้ผู้ใช้งานเห็น" />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>ประเภทภารกิจ *</FieldLabel>
                <StyledSelect value={formValues.category} onChange={v => setField('category', v)} options={[{ value:'', label:'เลือกประเภท' }, ...categoryOptions]} />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Crontab *</FieldLabel>
                <StyledInput value={formValues.crontab} onChange={e => setField('crontab', e.target.value)} placeholder="0 7 * * MON" />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>คำอธิบายภารกิจ *</FieldLabel>
                <StyledTextarea value={formValues.description} onChange={e => setField('description', e.target.value)} placeholder="ประโยคที่อธิบายเงื่อนไขของภารกิจ..." rows={4} />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>ลำดับ (น้อยอยู่บน) *</FieldLabel>
                <StyledNumber value={formValues.order} onChange={v => setField('order', v)} min={0} />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Tip *</FieldLabel>
                <StyledTextarea value={formValues.tip} onChange={e => setField('tip', e.target.value)} placeholder="คุณสามารถเริ่มพิชิตเลเวลได้ทุกวันศุกร์..." rows={2} />
              </FieldWrap>

              <FieldWrap>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', color: tokens.textMain, fontSize:14 }}>
                  <input type="checkbox" checked={!!formValues.kyc_required} onChange={e => setField('kyc_required', e.target.checked)}
                         style={{ width:16, height:16, accentColor: tokens.primary, cursor:'pointer' }}
                  />
                  ต้องทำ KYC ก่อน
                </label>
              </FieldWrap>
            </div>

            {/* Right column */}
            <div>
              <FieldWrap>
                <FieldLabel>พาร์ทเนอร์ที่รองรับ</FieldLabel>
                <PartnerBox>
                  {(partnerGroups || []).map(pg => {
                    const groupIds = pg.partners.map(p => p.id);
                    const allChecked = groupIds.every(id => checkedPartners.includes(id));
                    const someChecked = groupIds.some(id => checkedPartners.includes(id));
                    return (
                      <div key={pg.id} style={{ marginBottom: 8 }}>
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', color: tokens.textMain, fontWeight:600, fontSize:13, marginBottom:4 }}>
                          <input type="checkbox" checked={allChecked} ref={el => el && (el.indeterminate = someChecked && !allChecked)}
                                 onChange={() => toggleGroup(pg.partners)}
                                 style={{ width:14, height:14, accentColor: tokens.primary, cursor:'pointer' }}
                          />
                          {pg.name}
                        </label>
                        <div style={{ paddingLeft: 20 }}>
                          {pg.partners.map(p => (
                            <label key={p.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', color: tokens.textSub, fontSize:13, marginBottom:3 }}>
                              <input type="checkbox" checked={checkedPartners.includes(p.id)} onChange={() => togglePartner(p.id)}
                                     style={{ width:14, height:14, accentColor: tokens.primary, cursor:'pointer' }}
                              />
                              {p.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </PartnerBox>
              </FieldWrap>

              <Grid2 style={{ gap: 16, marginTop: 0 }}>
                <FieldWrap>
                  <FieldLabel>Whitelists (เบอร์โทร)</FieldLabel>
                  <StyledTextarea value={formValues.whitelists} onChange={e => setField('whitelists', e.target.value)} rows={3} />
                </FieldWrap>
                <FieldWrap>
                  <FieldLabel>Blacklists (เบอร์โทร)</FieldLabel>
                  <StyledTextarea value={formValues.blacklists} onChange={e => setField('blacklists', e.target.value)} rows={3} />
                </FieldWrap>
                <FieldWrap>
                  <FieldLabel>Whitelist CIDs (บัตรประชาชน)</FieldLabel>
                  <StyledTextarea value={formValues.whitelist_cids} onChange={e => setField('whitelist_cids', e.target.value)} rows={3} />
                </FieldWrap>
                <FieldWrap>
                  <FieldLabel>Blacklist CIDs (บัตรประชาชน)</FieldLabel>
                  <StyledTextarea value={formValues.blacklist_cids} onChange={e => setField('blacklist_cids', e.target.value)} rows={3} />
                </FieldWrap>
              </Grid2>
            </div>
          </Grid2>
        </SectionBody>
      </SectionCard>

      {/* ── Levels & Tasks ── */}
      <SectionCard>
        <SectionHeader>
          <SectionTitle>เลเวลและภารกิจย่อย</SectionTitle>
          <GhostBtn onClick={addLevel}><PlusOutlined /> เพิ่มเลเวล</GhostBtn>
        </SectionHeader>
        <SectionBody>
          {activeLevels.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 0', color: tokens.textMuted, fontSize:14 }}>
              ยังไม่มีเลเวล — คลิก &ldquo;เพิ่มเลเวล&rdquo; เพื่อเริ่มต้น
            </div>
          ) : (
            <LevelList>
              {activeLevels.map((level, levelIndex) => {
                const accentColor = levelColors[levelIndex % levelColors.length];
                const activeTasks = (level.task_v2s || []).filter(t => !t._destroy);
                return (
                  <Collapsible
                    key={level.id}
                    accent={accentColor}
                    defaultOpen={levelIndex === 0}
                    header={() => (
                      <PanelTitle>
                        <LevelBadge $color={accentColor}>Lv.{level.no}</LevelBadge>
                        {level.name}
                        <span style={{ color: tokens.textMuted, fontWeight:400, fontSize:12 }}>
                          · {activeTasks.length} ด่าน
                        </span>
                      </PanelTitle>
                    )}
                  >
                    <Grid2 style={{ marginBottom: 16 }}>
                      <FieldWrap style={{ margin: 0 }}>
                        <FieldLabel>ชื่อเลเวล</FieldLabel>
                        <StyledInput value={level.name} onChange={e => updateLevel(level.id, 'name', e.target.value)} />
                      </FieldWrap>
                      <FieldWrap style={{ margin: 0 }}>
                        <FieldLabel>หมายเลขเลเวล</FieldLabel>
                        <StyledNumber value={level.no} onChange={v => updateLevel(level.id, 'no', v)} min={1} />
                      </FieldWrap>
                    </Grid2>

                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                      <SubHeading>ด่านย่อย (Tasks)</SubHeading>
                      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <GhostBtn style={{ fontSize:12, padding:'5px 12px' }} onClick={() => addTask(level.id)}>
                          <PlusOutlined /> เพิ่มด่าน
                        </GhostBtn>
                        <Popconfirm title="ลบเลเวลนี้?" onConfirm={() => deleteLevel(level.id)}>
                          <DangerBtn><DeleteOutlined /> ลบเลเวล</DangerBtn>
                        </Popconfirm>
                      </div>
                    </div>

                    <TaskList>
                      {activeTasks.map((task, taskIndex) => (
                        <TaskCollapsible
                          key={task.id}
                          header={() => (
                            <PanelTitle style={{ fontSize:13 }}>
                              <span style={{ color: tokens.textMuted, fontWeight:400 }}>#{task.no}</span>
                              {task.name}
                              {task.coin > 0 && <CoinBadge>{task.coin}</CoinBadge>}
                            </PanelTitle>
                          )}
                        >
                          <Grid2>
                            <div>
                              <FieldWrap>
                                <FieldLabel>ชื่อด่าน</FieldLabel>
                                <StyledInput value={task.name} onChange={e => updateTask(level.id, task.id, 'name', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>เนื้อหาด่าน</FieldLabel>
                                <StyledTextarea rows={4} value={task.content} onChange={e => updateTask(level.id, task.id, 'content', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>จำนวนเหรียญ ◈</FieldLabel>
                                <StyledNumber min={0} value={task.coin} onChange={v => updateTask(level.id, task.id, 'coin', v)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>หน่วงเวลาก่อนทำการบ้าน (วินาที)</FieldLabel>
                                <StyledNumber min={0} value={task.delay_time} onChange={v => updateTask(level.id, task.id, 'delay_time', v)} />
                              </FieldWrap>
                            </div>
                            <div>
                              <FieldWrap>
                                <FieldLabel>YouTube Video ID</FieldLabel>
                                <StyledInput value={task.youtube_id} onChange={e => updateTask(level.id, task.id, 'youtube_id', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>หัวข้อแจ้งเตือน (ถูก)</FieldLabel>
                                <StyledInput value={task.notification_title_correct} onChange={e => updateTask(level.id, task.id, 'notification_title_correct', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>เนื้อหาแจ้งเตือน (ถูก)</FieldLabel>
                                <StyledInput value={task.notification_body_correct} onChange={e => updateTask(level.id, task.id, 'notification_body_correct', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>หัวข้อแจ้งเตือน (ผิด)</FieldLabel>
                                <StyledInput value={task.notification_title_wrong} onChange={e => updateTask(level.id, task.id, 'notification_title_wrong', e.target.value)} />
                              </FieldWrap>
                              <FieldWrap>
                                <FieldLabel>เนื้อหาแจ้งเตือน (ผิด)</FieldLabel>
                                <StyledInput value={task.notification_body_wrong} onChange={e => updateTask(level.id, task.id, 'notification_body_wrong', e.target.value)} />
                              </FieldWrap>
                            </div>
                          </Grid2>

                          <Divider />

                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                            <SubHeading>แบบทดสอบ (Quizzes)</SubHeading>
                            <Popconfirm title="ลบด่านนี้?" onConfirm={() => deleteTask(level.id, task.id)}>
                              <DangerBtn><DeleteOutlined /> ลบด่านนี้</DangerBtn>
                            </Popconfirm>
                          </div>

                          <QuizFormNewV2
                            quizzes={task.quiz_v2s || task.quiz_v2s_attributes || []}
                            onQuizzesChange={q => updateTaskQuizzes(level.id, task.id, q)}
                          />
                        </TaskCollapsible>
                      ))}
                    </TaskList>
                  </Collapsible>
                );
              })}
            </LevelList>
          )}
        </SectionBody>
      </SectionCard>
    </Root>
  );
};

export default MissionEditApp;